// apps/widget/src/lib/upload.ts
//
// Downscale, encode, presigned PUT straight to R2. The image never touches our
// Node process, which keeps the API responsive while a phone on 3G finishes
// uploading.

import { IMAGE } from '@skinvizo/shared';
import { api } from './api';

export interface UploadResult {
  imageKey: string;
  bytes: number;
}

/**
 * Longest edge to 1280px. Larger buys nothing — the provider sees a downscaled
 * image either way — and costs upload seconds the visitor spends watching a
 * progress bar.
 */
export async function downscale(source: HTMLCanvasElement): Promise<Blob> {
  const scale = Math.min(1, IMAGE.MAX_EDGE_PX / Math.max(source.width, source.height));

  if (scale >= 1) return encode(source);

  const canvas = document.createElement('canvas');
  canvas.width = Math.round(source.width * scale);
  canvas.height = Math.round(source.height * scale);

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas is unavailable');

  // High-quality resampling: nearest-neighbour downscaling introduces aliasing
  // that reads as texture, and texture is one of the things being measured.
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(source, 0, 0, canvas.width, canvas.height);

  return encode(canvas);
}

function encode(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Could not encode the photo'))),
      'image/jpeg',
      IMAGE.JPEG_QUALITY,
    );
  });
}

export async function uploadPhoto(
  sessionId: string,
  blob: Blob,
  onProgress?: (fraction: number) => void,
): Promise<UploadResult> {
  if (blob.size > IMAGE.MAX_UPLOAD_BYTES) {
    throw new Error('That photo is too large.');
  }

  const { uploadUrl, imageKey } = await api.uploadUrl(sessionId, 'image/jpeg');

  // XHR rather than fetch: fetch has no upload progress events, and on a slow
  // connection a progress bar is the difference between waiting and leaving.
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl, true);
    xhr.setRequestHeader('Content-Type', 'image/jpeg');

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) onProgress?.(e.loaded / e.total);
    });

    xhr.addEventListener('load', () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`Upload failed (${xhr.status})`)),
    );
    xhr.addEventListener('error', () => reject(new Error('Upload failed')));
    xhr.addEventListener('abort', () => reject(new Error('Upload cancelled')));

    xhr.send(blob);
  });

  onProgress?.(1);
  return { imageKey, bytes: blob.size };
}
